/**
 * save-search.js — « Sauvegarder cette recherche » (alerte e-mail).
 *
 * Une seule mécanique pour tous les déclencheurs : rangée des filtres, cloche de
 * la feuille mobile, zéro résultat (via `JaiioFacetsUI.triggerSaveSearch`) et
 * l'en-tête des résultats de recherche. Chacun appelle
 * `window.jaiioSaveSearch.open(bouton)` ; le popover s'ancre à ce bouton.
 *
 * Contrat avec le back, inchangé depuis la section flottante :
 * - POST same-origin `/apps/jaiio-api/saved-search` `{ url, searchQuery, filters }`.
 *   Le proxy d'application de Shopify signe la requête et ajoute
 *   `logged_in_customer_id` ; une URL absolue du back exigerait une signature
 *   HMAC secrète, impossible côté thème ;
 * - 409 = une recherche identique existe déjà ;
 * - la réponse porte `newsletterStatus` (Klaviyo) ; UNSUBSCRIBED et SUPPRESSED
 *   affichent un avertissement ;
 * - PATCH `/saved-search/:id` pour l'interrupteur e-mail et la fréquence.
 *
 * Balisage et libellés : `snippets/save-search.liquid`.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-save-search]');
  if (!root) return;

  var API = '/apps/jaiio-api/saved-search';
  // Q52 : trois jours par défaut. Le back crée l'alerte à sa propre valeur ; le
  // thème la corrige juste après la création (voir `applyDefaultFrequency`).
  var DEFAULT_FREQUENCY = 72;
  var KEY_MAPPING = {
    categorie_niveau_1: 'categorie',
    categorie_niveau_2: 'sous_categorie',
    categorie_niveau_3: 'sous_sous_categorie',
    price: 'prix'
  };

  var config = {};
  try {
    config = JSON.parse(root.querySelector('[data-save-search-config]').textContent);
  } catch (e) {
    console.error('[save-search] configuration illisible', e);
    return;
  }

  var popovers = {
    saved: root.querySelector('[data-save-search-popover="saved"]'),
    nofilter: root.querySelector('[data-save-search-popover="nofilter"]'),
    duplicate: root.querySelector('[data-save-search-popover="duplicate"]')
  };

  var anchor = null;       // bouton qui a ouvert le popover courant
  var savedKey = null;     // critères de la dernière recherche sauvegardée
  var busy = false;

  /* ── Critères ─────────────────────────────────────────────────────────── */

  // Moteur maison : `jaiioSearch.getFilters()` rend déjà le format canonique du
  // back. Pages au moteur natif : lecture des paramètres `filter.*` de Shopify.
  function readActiveFilters() {
    var readable = {};
    if (window.jaiioSearch && typeof window.jaiioSearch.getFilters === 'function') {
      var engine = window.jaiioSearch.getFilters() || {};
      Object.keys(engine).forEach(function (key) {
        var value = engine[key];
        if (key === 'prix') {
          if (value && (value.min != null || value.max != null)) readable.prix = value;
          return;
        }
        if (Array.isArray(value) && value.length) readable[key] = value;
      });
      return readable;
    }

    var params = new URLSearchParams(window.location.search);
    new Set(params.keys()).forEach(function (rawKey) {
      if (rawKey.indexOf('filter.p.m.custom.') !== 0) return;
      var name = rawKey.replace('filter.p.m.custom.', '');
      name = KEY_MAPPING[name] || name;
      var values = params.getAll(rawKey).filter(Boolean);
      if (values.length) readable[name] = values;
    });
    var min = params.get('filter.v.price.gte');
    var max = params.get('filter.v.price.lte');
    if (min !== null || max !== null) {
      readable.prix = { min: min !== null ? parseFloat(min) : null, max: max !== null ? parseFloat(max) : null };
    }
    return readable;
  }

  function buildPayload() {
    var filters = {};
    var context = config.context || {};
    Object.keys(context).forEach(function (key) {
      var value = context[key];
      if (value == null || value === '') return;
      // Un métachamp rend une chaîne ; le back attend un tableau de chaînes.
      filters[key] = Array.isArray(value) ? value : [value];
    });

    var prix = config.prix || {};
    var collectionMin = parseFloat(prix.min);
    var collectionMax = parseFloat(prix.max);
    if (collectionMin === 0 && isFinite(collectionMax)) filters.prix = { min: collectionMin, max: collectionMax };

    // La facette écrase le contexte : la visiteuse a volontairement raffiné ce critère.
    var active = readActiveFilters();
    Object.keys(active).forEach(function (key) { filters[key] = active[key]; });

    // Le back exige deux bornes numériques. Une facette à une seule borne
    // (« moins de 200 € ») ferait un 400 : le prix n'est alors pas persisté.
    if (filters.prix != null) {
      var lo = isFinite(filters.prix.min) && filters.prix.min !== null ? filters.prix.min : null;
      var hi = isFinite(filters.prix.max) && filters.prix.max !== null ? filters.prix.max : null;
      if (lo !== null && hi !== null) filters.prix = { min: lo, max: hi };
      else delete filters.prix;
    }

    return {
      url: config.url || window.location.href,
      searchQuery: new URLSearchParams(window.location.search).get('q'),
      filters: Object.keys(filters).length ? filters : null
    };
  }

  // Deux recherches sont « la même » si requête et critères coïncident — l'URL
  // porte aussi `take`, qui ne dit rien de la recherche.
  function keyOf(payload) {
    return JSON.stringify([payload.searchQuery || null, payload.filters]);
  }

  /* ── État « sauvegardée » ─────────────────────────────────────────────── */

  function syncSavedState() {
    var saved = savedKey !== null && savedKey === keyOf(buildPayload());
    document.documentElement.classList.toggle('search-saved', saved);
  }

  // Le moteur change les filtres sans recharger la page : une nouvelle
  // combinaison n'est plus « sauvegardée ». Branché au DOMContentLoaded, qui
  // suit tous les scripts différés — `jaiio-search.js` compris, quel que soit
  // l'ordre de chargement.
  function hookEngine() {
    if (window.jaiioSearch && typeof window.jaiioSearch.on === 'function') {
      window.jaiioSearch.on('change', syncSavedState);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hookEngine);
  else hookEngine();

  /* ── Placement ────────────────────────────────────────────────────────── */

  function visible(el) {
    if (!el || !el.isConnected) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function position(el) {
    if (!el.matches(':popover-open')) return;
    var pop = el.getBoundingClientRect();
    var gap = 12;
    var margin = 8;

    if (!visible(anchor)) {
      el.dataset.side = 'none';
      el.style.left = Math.max(margin, (window.innerWidth - pop.width) / 2) + 'px';
      el.style.top = Math.max(margin, window.innerHeight - pop.height - 16) + 'px';
      return;
    }

    var a = anchor.getBoundingClientRect();
    var above = a.top - pop.height - gap;
    var side = above >= margin ? 'above' : 'below';
    var top = side === 'above' ? above : Math.min(a.bottom + gap, window.innerHeight - pop.height - margin);
    var left = a.left + a.width / 2 - pop.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - pop.width - margin));

    el.dataset.side = side;
    el.style.top = Math.max(margin, top) + 'px';
    el.style.left = left + 'px';
    var arrow = a.left + a.width / 2 - left;
    el.style.setProperty('--arrow-left', Math.max(16, Math.min(arrow, pop.width - 16)) + 'px');
  }

  function show(name) {
    Object.keys(popovers).forEach(function (key) {
      if (key !== name) try { popovers[key].hidePopover(); } catch (e) { /* déjà fermé */ }
    });
    try { popovers[name].showPopover(); } catch (e) { return; /* Popover API absente */ }
    position(popovers[name]);
  }

  function repositionAll() {
    Object.keys(popovers).forEach(function (key) { position(popovers[key]); });
  }
  window.addEventListener('scroll', repositionAll, { passive: true });
  window.addEventListener('resize', repositionAll, { passive: true });

  root.addEventListener('click', function (event) {
    var close = event.target.closest('[data-save-search-close]');
    if (!close) return;
    try { close.closest('[popover]').hidePopover(); } catch (e) { /* noop */ }
  });

  /* ── Réseau ───────────────────────────────────────────────────────────── */

  function post(payload) {
    return fetch(API, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.text().then(function (raw) {
        var data = null;
        try { data = JSON.parse(raw); } catch (e) { /* réponse non JSON */ }
        return { ok: res.ok, status: res.status, data: data };
      });
    }, function (error) {
      return { ok: false, status: 0, data: null, error: error };
    });
  }

  function patch(id, body) {
    return fetch(API + '/' + id, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('PATCH saved-search/' + id + ' : ' + res.status);
    });
  }

  // Le back enveloppe ses erreurs dans un HTTP 200 `{ statusCode: 500, … }` :
  // seul un corps `status: "SUBSCRIBED"` vaut succès.
  function subscribeNewsletter() {
    return fetch('/apps/jaiio-api/newsletter-subscribe', { method: 'POST', credentials: 'same-origin' })
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (json) {
          if (!res.ok || !json || json.status !== 'SUBSCRIBED') {
            throw new Error('newsletter-subscribe : ' + res.status + ' ' + JSON.stringify(json));
          }
          return json;
        });
      });
  }

  /* ── Popover « sauvegardée » ──────────────────────────────────────────── */

  // Les écouteurs du popover visent l'alerte qui vient d'être créée : ils sont
  // posés à chaque création et retirés à la fermeture.
  var controller = null;

  function wireSavedPopover(id, data) {
    if (controller) controller.abort();
    controller = new AbortController();
    var signal = controller.signal;
    var el = popovers.saved;
    var emailBtn = el.querySelector('[data-save-search-email]');
    var frequency = el.querySelector('[data-save-search-frequency]');
    var select = frequency.querySelector('select');

    var emailOn = !data || data.emailNotification !== false;
    emailBtn.setAttribute('aria-checked', String(emailOn));
    frequency.hidden = !emailOn;
    // Sans valeur renvoyée, celle du back par défaut — la même hypothèse que
    // la page « Mes recherches » (`saved-searches-list.liquid`).
    select.value = String((data && data.emailStamp) || 24);
    select.dataset.lastValue = select.value;

    emailBtn.addEventListener('click', function () {
      var next = emailBtn.getAttribute('aria-checked') !== 'true';
      emailBtn.disabled = true;
      emailBtn.setAttribute('aria-checked', String(next));
      frequency.hidden = !next;
      requestAnimationFrame(function () { position(el); });
      patch(id, { emailNotification: next }).catch(function () {
        emailBtn.setAttribute('aria-checked', String(!next));
        frequency.hidden = next;
        requestAnimationFrame(function () { position(el); });
      }).then(function () { emailBtn.disabled = false; });
    }, { signal: signal });

    select.addEventListener('change', function () {
      var previous = select.dataset.lastValue;
      select.disabled = true;
      patch(id, { emailStamp: Number(select.value) })
        .then(function () { select.dataset.lastValue = select.value; })
        .catch(function () { select.value = previous; })
        .then(function () { select.disabled = false; });
    }, { signal: signal });

    wireNewsletterWarning(data && data.newsletterStatus, signal);

    el.addEventListener('toggle', function (e) {
      if (e.newState === 'closed' && controller) controller.abort();
    }, { signal: signal });
  }

  function wireNewsletterWarning(status, signal) {
    var el = popovers.saved;
    var warning = el.querySelector('[data-save-search-warning]');
    var content = el.querySelector('[data-save-search-warning-content]');
    var success = el.querySelector('[data-save-search-warning-success]');
    var suppressed = el.querySelector('[data-save-search-warning-suppressed]');
    var resub = el.querySelector('[data-save-search-resubscribe]');
    var label = el.querySelector('[data-save-search-resubscribe-label]');
    var spinner = el.querySelector('[data-save-search-resubscribe-spinner]');

    // UNKNOWN (lookup Klaviyo échoué) ou SUBSCRIBED : rien à dire.
    warning.hidden = status !== 'UNSUBSCRIBED' && status !== 'SUPPRESSED';
    if (warning.hidden) return;

    // SUPPRESSED = blocage définitif côté Klaviyo (hard bounce, plainte, retrait
    // manuel) : réabonner ne lèverait rien et abîmerait la réputation d'envoi.
    var isSuppressed = status === 'SUPPRESSED';
    content.hidden = isSuppressed;
    success.hidden = true;
    suppressed.hidden = !isSuppressed;
    resub.disabled = false;
    spinner.hidden = true;
    label.hidden = false;
    if (isSuppressed) return;

    resub.addEventListener('click', function () {
      resub.disabled = true;
      spinner.hidden = false;
      label.hidden = true;
      subscribeNewsletter().then(function () {
        content.hidden = true;
        success.hidden = false;
        requestAnimationFrame(function () { position(el); });
      }, function (err) {
        console.error('[save-search] échec du réabonnement newsletter', err);
        resub.disabled = false;
        spinner.hidden = true;
        label.hidden = false;
      });
    }, { signal: signal });
  }

  // Q52. Échec silencieux : l'alerte existe, le sélecteur montre la valeur réelle.
  function applyDefaultFrequency(id, data) {
    data = data || {};
    if (!id || Number(data.emailStamp) === DEFAULT_FREQUENCY) return Promise.resolve(data);
    return patch(id, { emailStamp: DEFAULT_FREQUENCY }).then(function () {
      data.emailStamp = DEFAULT_FREQUENCY;
      return data;
    }, function (err) {
      console.warn('[save-search] fréquence par défaut non appliquée', err);
      return data;
    });
  }

  /* ── Ouverture ────────────────────────────────────────────────────────── */

  function open(trigger) {
    anchor = trigger || null;

    if (!config.logged_in) {
      var back = window.location.pathname + window.location.search;
      window.location.href = config.login_url + '?return_to=' + encodeURIComponent(back);
      return;
    }
    if (busy) return;

    var payload = buildPayload();
    var key = keyOf(payload);
    if (key === savedKey) { show('duplicate'); return; }
    if (!payload.searchQuery && !payload.filters) { show('nofilter'); return; }

    busy = true;
    if (anchor) anchor.setAttribute('aria-busy', 'true');
    post(payload).then(function (result) {
      if (result.status === 409) {
        savedKey = key;
        syncSavedState();
        show('duplicate');
        return;
      }
      if (!result.ok) {
        console.error('[save-search] enregistrement refusé', result.status, result.error || result.data);
        return;
      }
      savedKey = key;
      syncSavedState();
      var id = result.data && result.data.id;
      return applyDefaultFrequency(id, result.data).then(function (data) {
        if (id) wireSavedPopover(id, data);
        show('saved');
      });
    }).then(function () {
      busy = false;
      if (anchor) anchor.setAttribute('aria-busy', 'false');
    });
  }

  window.jaiioSaveSearch = { open: open };
})();
